import React, { useEffect, useState } from 'react';
import { DoorOpen, Eye, EyeOff, GraduationCap, Pencil, Plus, Trash, Users } from 'lucide-react';
import AdminSidebar from '../components/AdminSidebar';
import CreateLecturerModal from '../components/CreateLecturer.js';
import CreateRoomModal from '../components/CreateRoom.js';
import EditRoomModal from '../components/EditRoomModal.js';
import '../styles/AdminSettings.css';
import MultipleStudentsAdd from '../components/MultipleStudentsAdd.js';
import EditLecturerModal from '../components/EditLecturerModal.js';

export default function AdminSettings() {
    // State to hold loading state
    const [loading, setLoading] = useState(true);
    // State to hold number of user in roles
    const [userRolesCount, setUserRolesCount] = useState([]);
    // Define sections for settings
    const settingSections = [
        {
            id: 'users',
            name: 'Użytkownicy i role',
            icon: Users,
            description: 'Zarządzanie użytkownikami i rolami'
        },
        {
            id: 'lecturers',
            name: 'Wykładowcy',
            icon: GraduationCap,
            description: 'Zarządzanie wykładowcami'
        },
        {
            id: 'rooms',
            name: 'Sale',
            icon: DoorOpen,
            description: 'Zarządzanie salami'
        },
        {
            id: 'Multistudents',
            name: 'Dodawanie wielu kursantów',
            icon: Users,
            description: 'Dodawanie wielu kursantów jednocześnie'
        }
    ]
    // State to hold active section
    const [activeSection, setActiveSection] = useState('users');
    // State to hold lecturers
    const [lecturers, setLecturers] = useState([]);
    // State to hold loading state for lecturers
    const [loadingLecturers, setLoadingLecturers] = useState(true);
    // State to show lecturer creation modal
    const [showCreateLecturerModal, setShowCreateLecturerModal] = useState(false);
    // State to hold rooms
    const [rooms, setRooms] = useState([]);
    // State to hold loading state for rooms
    const [loadingRooms, setLoadingRooms] = useState(true);
    // State to show room creation modal
    const [showCreateRoomModal, setShowCreateRoomModal] = useState(false);
    // State to show multi student upload modal
    const [showMultiStudentUploadModal, setShowMultiStudentUploadModal] = useState(false);
    // Stete to hold users
    const [users, setUsers] = useState([]);
    // State to hold loading state for users
    const [loadingUsers, setLoadingUsers] = useState(true);
    // Helper to get password
    const currentYear = new Date().getFullYear();
    // Show hide password
    const [visiblePasswords, setVisiblePasswords] = useState({});
    // Room edit modal
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [showEditRoomModal, setShowEditRoomModal] = useState(false);

    const handleRoomClick = (room) => {
      setSelectedRoom(room);
      setShowEditRoomModal(true);
    };

    const generatePassword = (user) => {
        const first = user.first_name.charAt(0).toUpperCase() + user.first_name.slice(1, 3).toLowerCase();
        const last = user.last_name.charAt(0).toUpperCase() + user.last_name.slice(1, 3).toLowerCase();

        if (user.pesel && user.pesel.length >= 5) {
            const peselPart = user.pesel.slice(-5);
            return `${first}${last}@${peselPart}`;
        } else {
            return `${first}${last}@${currentYear}`;
        }
    };

    const togglePassword = (userId) => {
        setVisiblePasswords(prev => ({
            ...prev,
            [userId]: !prev[userId]
        }));
    };

    useEffect(() => {
         async function fetchRoles() {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/settings/roles`);
                const data = await response.json();
                
                const roleMap = {
                    1: 'admin',
                    2: 'lecturer',
                    3: 'student'
                };

                const roles = [
                    { id: 'admin', name: 'Administrator', description: 'Pełny dostęp do systemu', count: 0 },
                    { id: 'lecturer', name: 'Wykładowca', description: 'Zarządzanie kursami i uczniami', count: 0 },
                    { id: 'student', name: 'Kursant', description: 'Dostęp do własnych danych', count: 0 },
                ];

                data.forEach(item => {
                  const roleId = roleMap[item.role_id];
                  if (roleId) {
                    const role = roles.find(r => r.id === roleId);
                    if (role) {
                      role.count = Number(item.count);
                    }
                  }
                });

                setUserRolesCount(roles);

            } catch (error) {
                console.error('Error fetching user roles:', error);
            } finally {
                setLoading(false);
            }
         }

         async function fetchLecturers() {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/settings/lecturers`);
                const data = await response.json();

                const formatted = Array.isArray(data)
                    ? data.map(item => ({
                        ...item,
                        students_count: Number(item.students_count) || 0
                    }))
                : [];

                setLecturers(formatted);
            } catch (error) {
                console.error('Error fetching lecturers:', error);
            } finally {
                setLoadingLecturers(false);
            }
         }

         async function fetchRooms() {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/getData/rooms`);
                const data = await response.json();

                setRooms(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching rooms:', error);
            } finally {
                setLoadingRooms(false);
            }
        }

        async function fetchUsers() {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/settings/users`);
                const data = await response.json();

                setUsers(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching users:', error);
            } finally {
                setLoadingUsers(false);
            }
        }

        fetchRoles();
        fetchLecturers();
        fetchRooms();
        fetchUsers();
    }, []);

    const [selectedRole, setSelectedRole] = useState(null);
    const [roleUsers, setRoleUsers] = useState([]);

    const fetchUsersByRole = async (roleId) => {
      setLoadingUsers(true);
      try {
            const roleMap = {
                admin: 1,
                lecturer: 2,
                student: 3
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/settings/roles/${roleMap[roleId]}`);
            const data = await response.json();
            setRoleUsers(data);
        } catch (error) {
            console.error('Błąd pobierania użytkowników roli:', error);
        } finally {
            setLoadingUsers(false);
        }
    };


    const renderUsersSettings = () => {
        return (
            <>
                <div className='users-settings'>
                    <Users size={20} />
                    <div>
                        <h4 className='users-settings-title'>Zarządzanie rolami użytkowników</h4>
                        <p className='users-settings-description'>Konfiguruj uprawnienia i role w systemie</p>
                    </div>
                </div>
                {userRolesCount.map(role => (
                  <div 
                    key={role.id} 
                    className='user-role-card clickable' 
                    onClick={() => {
                      setSelectedRole(role);
                      fetchUsersByRole(role.id);
                    }}
                  >
                    <div className='user-role-header'>
                      <div>
                        <h4 className='user-role-title'>{role.name}</h4>
                        <p className='user-role-description'>{role.description}</p>
                      </div>
                      <div className='user-role-count'>
                        <span className='user-badge user-badge-info'>{role.count} użytkowników</span>
                      </div>
                    </div>
                  </div>
                ))}
                {selectedRole && (
                  <div className="role-users-section">
                    <h3>Użytkownicy roli: {selectedRole.name}</h3>
                    {loadingUsers ? (
                      <p>Ładowanie danych...</p>
                    ) : roleUsers.length > 0 ? (
                      <table className="users-table">
                        <thead>
                          <tr>
                            <th>Nazwa użytkownika</th>
                            <th>Imię</th>
                            <th>Nazwisko</th>
                            <th>Email</th>
                          </tr>
                        </thead>
                        <tbody>
                          {roleUsers.map((user, index) => (
                            <tr key={index}>
                              <td>{user.username}</td>
                              <td>{user.first_name}</td>
                              <td>{user.last_name}</td>
                              <td>{user.email}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p>Brak użytkowników w tej roli.</p>
                    )}
                  </div>
                )}
            </>  
        );
    }

    // Edit lecturer modal
    const [selectedLecturer, setSelectedLecturer] = useState(null);
    const [showEditLecturerModal, setShowEditLecturerModal] = useState(false);

    const handleDeleteLecturer = (lecturerId) => {
        if (window.confirm("Czy na pewno chcesz usunąć tego wykładowcę?")) {
            fetch(`${process.env.REACT_APP_API_URL}/settings/lecturer/${lecturerId}`, {
                method: 'DELETE'
            })
            .then(response => {
                if (response.ok) {
                    setLecturers(prev => prev.filter(l => l.lecturer_id !== lecturerId));
                } else {
                    console.error('Błąd podczas usuwania wykładowcy');
                }
            })
            .catch(error => {
                console.error('Błąd podczas usuwania wykładowcy:', error);
            });
        }
    };

    const handleEditLecturer = (lecturer) => {
        setSelectedLecturer(lecturer);
        setShowEditLecturerModal(true);
    };

    const renderLecturers = () => {
        return (
            <div>
                <GraduationCap size={20} />
                <div>
                    <h4 className='users-settings-title'>Zarządzanie wykładowcami</h4>
                    <p className='users-settings-description'>Zarządzanie wykładowcami w systemie</p>
                </div>
                <div className='settings-button-container'>
                    <button className='button button-primary' onClick={() => setShowCreateLecturerModal(true)}>
                        <Plus size={16} />
                        <span>Dodaj wykładowcę</span>
                    </button>
                </div>
                {showCreateLecturerModal && <CreateLecturerModal onAddRoom={(newRoom) => setRooms(prev => [...prev, newRoom])} onClose={() => setShowCreateLecturerModal(false)} />}
                <div className='lecturers-list-container'>
                    <table className='lecturers-list'>
                        <thead>
                            <tr>
                                <th>Imię i nazwisko</th>
                                <th>E-mail</th>
                                <th>Kursanci</th>
                                <th>Akcje</th>
                            </tr>
                        </thead>
                        <tbody>
                        {loadingLecturers ? (
                            <tr><td colSpan="3">Ładowanie wykładowców...</td></tr>
                            ) : lecturers.length === 0 ? (
                             <tr><td colSpan="3" style={{ color: '#64748b', textAlign: 'center', fontStyle: 'italic', padding: '12px 0' }}>Brak wykładowców</td></tr>
                            ) : (
                                lecturers.map(lecturer => (
                                    <tr key={lecturer.lecturer_id}>
                                        <td className='lecturer-td'>
                                            <div className='lecturer-name'>
                                                {lecturer.academic_position 
                                                    ? `${lecturer.academic_position}. ${lecturer.first_name} ${lecturer.last_name}` 
                                                    : `${lecturer.first_name} ${lecturer.last_name}`
                                                }
                                            </div>
                                        </td>
                                        <td className='lecturer-td'>
                                            <div className='lecturer-email'>
                                                {lecturer.email}
                                            </div>
                                        </td>
                                        <td className='lecturer-td'>
                                            <div className='lecturer-students'>
                                                {lecturer.students_count}
                                            </div>
                                        </td>
                                        <td className='lecturer-td'>
                                            <div className="action-buttons">
                                                <button className="icon-button edit" onClick={() => handleEditLecturer(lecturer)}>
                                                    <Pencil size={16} />
                                                </button>
                                                 <button className="icon-button delete" onClick={() => handleDeleteLecturer(lecturer.lecturer_id)}>
                                                    <Trash size={16} color="#dc2626" />
                                                 </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {showEditLecturerModal && selectedLecturer && (
                    <EditLecturerModal
                        lecturer={selectedLecturer}
                        onClose={() => setShowEditLecturerModal(false)}
                        onSave={(updatedLecturer) => {
                            setLecturers(prev => prev.map(l => l.lecturer_id === updatedLecturer.lecturer_id ? updatedLecturer : l));
                        }}
                    />
                )}
            </div>
        );
    }

    const renderRooms = () => {
        return (
            <div>
                <DoorOpen size={20} />
                <div>
                    <h4 className='users-settings-title'>Zarządzanie salami</h4>
                    <p className='users-settings-description'>Zarządzanie salami w systemie</p>
                </div>
                <div className='settings-button-container'>
                    <button className='button button-primary' onClick={() => setShowCreateRoomModal(true)}>
                        <Plus size={16} />
                        Dodaj salę
                    </button>
                </div>
                {showCreateRoomModal && <CreateRoomModal onClose={() => setShowCreateRoomModal(false)} onAddRoom={(newRoom) => setRooms(prev => [...prev, newRoom])} />}
                <div className="rooms-list">
                    {loadingRooms ? (
                        <p>Ładowanie sal...</p>
                    ) : rooms.length === 0 ? (
                        <p>Brak sal</p>
                    ) : (
                        rooms.map(room => (
                            <div key={room.room_id} className="room-card" onClick={() => handleRoomClick(room)}>
                                <h5 className="room-name">{room.room_name}</h5>
                                <p className="room-capacity">Lokalizacja: {room.location}</p>
                                <p className="room-capacity">Komentarz: {room.comment || 'Brak'}</p>
                            </div>
                        ))
                    )}
                </div>
                {showEditRoomModal && selectedRoom && (
                  <EditRoomModal
                    room={selectedRoom}
                    onClose={() => setShowEditRoomModal(false)}
                    onSave={async (updatedRoom) => {
                      try {
                        const response = await fetch(`${process.env.REACT_APP_API_URL}/settings/room/${updatedRoom.room_id}`, {
                          method: "PUT",
                          headers: {
                            "Content-Type": "application/json"
                          },
                          body: JSON.stringify({
                            room_name: updatedRoom.room_name,
                            location: updatedRoom.location,
                            comment: updatedRoom.comment
                          })
                        });
                    
                        if (!response.ok) throw new Error("Błąd aktualizacji sali");
                    
                        setRooms(prevRooms => prevRooms.map(r => r.room_id === updatedRoom.room_id ? updatedRoom : r));
                    
                      } catch (error) {
                        console.error(error);
                        alert("Nie udało się zaktualizować sali.");
                      } finally {
                        setShowEditRoomModal(false);
                      }
                    }}
                    onDelete={async (roomId) => {
                      try {
                        const response = await fetch(`${process.env.REACT_APP_API_URL}/settings/room/${roomId}`, {
                          method: "DELETE"
                        });
                        if (!response.ok) throw new Error("Błąd usuwania sali");
                        setRooms(prevRooms => prevRooms.filter(r => r.room_id !== roomId));
                      } catch (error) {
                        console.error(error);
                        alert("Nie udało się usunąć sali.");
                      } finally {
                        setShowEditRoomModal(false);
                      }
                    }}
                  />
                )}
            </div>
        );
    }

    const renderMultiStudentUpload = () => {
        return (
            <div>
                <Users size={20} />
                <div>
                    <h4 className='users-settings-title'>Dodawanie wielu kursantów</h4>
                    <p className='users-settings-description'>Dodaj wielu kursantów jednocześnie</p>
                </div>
                <div className='settings-button-container'>
                    <button className='button button-primary' onClick={() => setShowMultiStudentUploadModal(true)}>
                        <Plus size={16} />
                        Dodaj kursantów
                    </button>
                </div>
                {showMultiStudentUploadModal && <MultipleStudentsAdd onClose={() => setShowMultiStudentUploadModal(false)} />}
                <h3>Lista użytkowników stworzonych w ciągu ostatnich 7 dni</h3>
                <div className='lecturers-list-container'>
                    <table className='lecturers-list'>
                        <thead>
                            <tr>
                                <th className='users-list-header'>Nazwa użytkownika</th>
                                <th className='users-list-header'>Hasło</th>
                                <th className='users-list-header-action'>Akcja</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loadingUsers ? (
                                <tr><td colSpan="3">Ładowanie użytkowników...</td></tr>
                            ) : users.length === 0 ? (
                                <tr><td colSpan="3" style={{ color: '#64748b', textAlign: 'center', fontStyle: 'italic', padding: '12px 0' }}>Brak użytkowników</td></tr>
                            ) : (
                                users.map(user => {
                                    const password = generatePassword(user);
                                    const isVisible = visiblePasswords[user.user_id] || false;
                                    
                                    return (
                                        <tr key={user.user_id}>
                                            <td className='users-list-cell'>{user.username}</td>
                                            <td className='users-list-cell'>{isVisible ? password : '••••••••••'}</td>
                                            <td className='users-list-cell-action'>
                                                <button
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                                    onClick={() => togglePassword(user.user_id)}
                                                >
                                                    {visiblePasswords[user.user_id] ? (
                                                        <EyeOff size={16}  color='#374151'/>
                                                    ) : (
                                                        <Eye size={16} color='#374151' />
                                                    )}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }
    
    const renderContent = () => {
        switch (activeSection) {
            case 'users':
                return renderUsersSettings();
            case 'lecturers':
                return renderLecturers();
            case 'rooms':
                return renderRooms();
            case 'Multistudents':
                return renderMultiStudentUpload();
            default:
                return null;
        }
    }

    if (loading) {
        return <div>Ładowanie danych...</div>;
    }

    return (
        <div className='admin-dashboard'>
            {/* Sidebar */}
            <AdminSidebar />
            {/* Main content */}
            <div className='main-content'>
                <div className='header'>
                    <h1 className='header-title'>Ustawienia</h1>
                </div>
                <div className='dashboard'>
                    <h2 className='page-title'>Panel Ustawień</h2>
                    <p className='page-subtitle'>Kompleksowa konfiguracja systemu i uprawnień</p>
                    { /* Main Content */ }
                    <div className='settings-layout'>
                        <div className='settings-sidebar'>
                            <h3 className='settings-sidebar-title'>Kategorie ustawień</h3>
                            <div className='settings-sidebar-list'>
                                {settingSections.map(section => (
                                    <div
                                        key={section.id}
                                        className={`section-item ${activeSection === section.id ? 'active' : ''}`}
                                        onClick={() => setActiveSection(section.id)}
                                    >
                                        <div className='settings-sidebar-icon'>
                                            <section.icon size={20} />
                                        </div>
                                        <div className='settings-sidebar-name'>{section.name}</div>
                                        <div className='settings-sidebar-description'>{section.description}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* Content */}
                        <div className='settings-content'>
                            <div className='setting-content-header'>
                                <h2 className='settings-content-title'>{settingSections.find(s => s.id === activeSection)?.name}</h2>
                            </div>
                            {renderContent()}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}